import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  BackendResponse,
  ContestViewContest,
  ContestViewEntry,
  CONTEST_ENTRY_AWARD_STATUSES,
  Datastore,
  User,
} from '@freelancer/datastore';
import { ModalRef } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { FileDisplaySize } from '@freelancer/ui/file-display';
import { HeadingSize, HeadingType } from '@freelancer/ui/heading';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { Margin } from '@freelancer/ui/margin';
import {
  RibbonColor,
  RibbonOrientation,
  RibbonPlacement,
} from '@freelancer/ui/ribbon';
import { Tab } from '@freelancer/ui/tabs';
import {
  FontColor,
  FontSize,
  FontWeight,
  TextTransform,
} from '@freelancer/ui/text';
import { ThumbnailViewerImage } from '@freelancer/ui/thumbnail-viewer';
import { AvatarSize } from '@freelancer/ui/user-avatar';
import { EntryStatusApi } from 'api-typings/contests/contests';
import * as Rx from 'rxjs';
import { PreviewModalResponse } from './preview-modal.types';

@Component({
  selector: 'app-preview-modal',
  template: `
    <fl-link class="EntryPreview-navigator EntryPreview-navigator--previous">
      <fl-icon
        [name]="'ui-chevron-left'"
        [color]="IconColor.LIGHT"
        [size]="IconSize.XLARGE"
      ></fl-icon>
    </fl-link>

    <fl-link class="EntryPreview-navigator EntryPreview-navigator--next">
      <fl-icon
        [name]="'ui-chevron-right'"
        [color]="IconColor.LIGHT"
        [size]="IconSize.XLARGE"
      ></fl-icon>
    </fl-link>

    <fl-grid class="EntryPreview-layout">
      <fl-col class="EntryPreview-layout--primary" [col]="8">
        <fl-bit class="EntryPreview-header">
          <fl-bit class="EntryPreview-header-content">
            <fl-bit
              class="EntryPreview-header-content-title"
              [flMarginRight]="Margin.SMALL"
            >
              <fl-heading
                i18n="Entry number header"
                [size]="HeadingSize.SMALL"
                [headingType]="HeadingType.H1"
                [flMarginRight]="Margin.XXSMALL"
              >
                Entry #{{ entry.number }}
              </fl-heading>
              <fl-icon
                *ngIf="isSealed()"
                [name]="'ui-lock-closed'"
                [size]="IconSize.SMALL"
                [color]="IconColor.INHERIT"
              ></fl-icon>
            </fl-bit>

            <fl-rating
              [control]="ratingControl"
              [hideValue]="true"
              [readOnly]="
                !isContestOwner ||
                (rateResponse$ && (rateResponse$ | async) === null)
              "
            ></fl-rating>
          </fl-bit>

          <fl-bit class="EntryPreview-header-cta">
            <fl-bit>
              <fl-text
                i18n="Entry attachments text"
                [size]="FontSize.XSMALL"
                [color]="FontColor.MID"
                [textTransform]="TextTransform.UPPERCASE"
                [flMarginBottom]="Margin.XXXSMALL"
              >
                Attachments
              </fl-text>
              <fl-thumbnail-viewer
                class="EntryPreview-header-cta-thumbnails"
                [images]="thumbnails"
                [slidesToShow]="THUMBNAIL_LIMIT"
                (slideChange)="onSlideChange($event)"
              ></fl-thumbnail-viewer>
            </fl-bit>

            <fl-bit class="EntryPreview-header-cta-container">
              <ng-container *ngIf="(rateResponse$ | async) as response">
                <app-preview-modal-error
                  *ngIf="response.errorCode"
                  [flMarginBottom]="Margin.XXXSMALL"
                  [rateResponse]="response"
                ></app-preview-modal-error>
              </ng-container>

              <fl-bit
                class="EntryPreview-header-cta-group"
                *ngIf="isContestOwner"
                [flMarginBottom]="Margin.XSMALL"
              >
                <ng-container *ngIf="isActive()">
                  <fl-button
                    class="EntryPreview-header-cta-button"
                    i18n="Entry reject button"
                    [color]="ButtonColor.DEFAULT"
                    [size]="ButtonSize.SMALL"
                    [flMarginRight]="Margin.SMALL"
                  >
                    Reject
                  </fl-button>
                  <fl-button
                    class="EntryPreview-header-cta-button"
                    i18n="Entry award button"
                    [color]="ButtonColor.SUCCESS"
                    [size]="ButtonSize.SMALL"
                    (click)="onAwardEntry()"
                  >
                    Award
                  </fl-button>
                </ng-container>

                <fl-button
                  class="EntryPreview-header-cta-button"
                  *ngIf="isRejected()"
                  i18n="Entry view handover button"
                  [color]="ButtonColor.DEFAULT"
                  [size]="ButtonSize.SMALL"
                >
                  Reconsider
                </fl-button>

                <fl-button
                  class="EntryPreview-header-cta-button"
                  *ngIf="isAwarded() || isBought()"
                  i18n="Entry view handover button"
                  [color]="ButtonColor.DEFAULT"
                  [size]="ButtonSize.SMALL"
                >
                  View Handover
                </fl-button>

                <fl-button
                  class="EntryPreview-header-cta-button"
                  *ngIf="isBeingSold()"
                  i18n="Entry buy button"
                  [color]="ButtonColor.DEFAULT"
                  [size]="ButtonSize.SMALL"
                >
                  Buy
                </fl-button>
              </fl-bit>
            </fl-bit>
          </fl-bit>
        </fl-bit>
        <fl-bit class="EntryPreview-file-container">
          <fl-bit class="EntryPreview-file-overlay" *ngIf="isRejected()">
            <fl-text
              class="EntryPreview-file-overlay-text"
              i18n="Entry rejected text"
              [color]="FontColor.LIGHT"
              [weight]="FontWeight.BOLD"
            >
              Rejected
            </fl-text>
          </fl-bit>
          <fl-ribbon
            class="Card-ribbon"
            *ngIf="isAwarded()"
            iconLabel="Awarded"
            [color]="RibbonColor.SECONDARY"
            [icon]="'ui-trophy'"
            [orientation]="RibbonOrientation.VERTICAL"
            [placement]="RibbonPlacement.LEFT"
          ></fl-ribbon>
          <fl-ribbon
            class="Card-ribbon"
            *ngIf="isBought()"
            iconLabel="Bought"
            [color]="RibbonColor.SECONDARY_DARK"
            [icon]="'ui-bought-medal'"
            [orientation]="RibbonOrientation.VERTICAL"
            [placement]="RibbonPlacement.LEFT"
          ></fl-ribbon>
          <fl-ribbon
            class="Card-ribbon"
            *ngIf="isHighlighted()"
            iconLabel="Highlighted"
            i18n="Highlighted text"
            [color]="RibbonColor.SECONDARY"
            [placement]="RibbonPlacement.LEFT"
          >
            Highlighted
          </fl-ribbon>
          <fl-bit class="EntryPreview-file-content">
            <fl-picture
              *ngIf="isImage(entry.files[currentFileIndex]?.fileUrl)"
              alt="{{ entry.files[currentFileIndex]?.name }}"
              [src]="entry.files[currentFileIndex]?.thumbnail900Url"
              [externalSrc]="true"
              [boundedWidth]="true"
            ></fl-picture>
            <fl-video
              class="EntryPreview-file-video"
              *ngIf="isVideo(entry.files[currentFileIndex]?.fileUrl)"
              [src]="entry.files[currentFileIndex]?.fileUrl"
            ></fl-video>
          </fl-bit>
        </fl-bit>
      </fl-col>
      <fl-col class="EntryPreview-layout--secondary" [col]="4">
        <fl-bit class="EntryPreview-details">
          <fl-bit
            class="EntryPreview-details-user"
            [ngClass]="{
              'EntryPreview-details-user--centerAlign': isContestOwner
            }"
            [flMarginBottom]="Margin.SMALL"
          >
            <fl-user-avatar
              [users]="[freelancer]"
              [size]="AvatarSize.LARGE"
              [flMarginRight]="Margin.XSMALL"
            ></fl-user-avatar>
            <fl-bit>
              <fl-bit
                class="EntryPreview-details-user-info"
                [flMarginBottom]="Margin.XXSMALL"
              >
                <!-- TODO: Fix logic on T81708  -->
                <fl-flag
                  *ngIf="false"
                  [country]="country"
                  [flMarginRight]="Margin.XXSMALL"
                ></fl-flag>
                <fl-text [weight]="FontWeight.BOLD">
                  {{ freelancer.displayName }}
                </fl-text>
              </fl-bit>
              <fl-button
                *ngIf="isContestOwner"
                i18n="Chat button"
                [color]="ButtonColor.DEFAULT"
                [size]="ButtonSize.MINI"
                [flMarginBottom]="Margin.XXXSMALL"
                (click)="handleChatClick()"
              >
                <fl-online-indicator
                  [isOnline]="
                    freelancer.onlineOfflineStatus?.status === 'online'
                  "
                  [flMarginRight]="Margin.XXXSMALL"
                ></fl-online-indicator>
                Chat
              </fl-button>
            </fl-bit>
          </fl-bit>

          <fl-text
            class="EntryPreview-details-title"
            [weight]="FontWeight.BOLD"
            [flMarginBottom]="Margin.XSMALL"
          >
            {{ entry.title }}
          </fl-text>
          <fl-text
            class="EntryPreview-details-description"
            [readMore]="DESCRIPTION_LIMIT"
            [flMarginBottom]="Margin.SMALL"
          >
            {{ entry.description }}
          </fl-text>

          <fl-bit class="EntryPreview-details-feedback">
            <fl-icon
              [name]="'ui-thumbs-up'"
              [color]="entry.isLiked ? IconColor.PRIMARY : IconColor.MID"
              [size]="IconSize.SMALL"
              [flMarginRight]="Margin.XXXSMALL"
            ></fl-icon>
            <fl-text [flMarginRight]="Margin.XSMALL">
              {{ entry.likeCount || 0 }}
            </fl-text>
            <fl-icon
              [name]="'ui-community'"
              [color]="IconColor.MID"
              [size]="IconSize.SMALL"
              [flMarginRight]="Margin.XXXSMALL"
            ></fl-icon>
            <fl-text> {{ entry.commentCount }} </fl-text>
          </fl-bit>
        </fl-bit>

        <fl-bit class="EntryPreview-tab" [flMarginBottom]="Margin.SMALL">
          <fl-tabs [tabs]="tabs"> </fl-tabs>
        </fl-bit>
      </fl-col>
    </fl-grid>
  `,
  styleUrls: ['./preview-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewModalComponent implements OnInit, OnDestroy {
  AvatarSize = AvatarSize;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  FileDisplaySize = FileDisplaySize;
  FontColor = FontColor;
  FontSize = FontSize;
  FontWeight = FontWeight;
  HeadingSize = HeadingSize;
  HeadingType = HeadingType;
  IconColor = IconColor;
  IconSize = IconSize;
  Margin = Margin;
  RibbonColor = RibbonColor;
  RibbonOrientation = RibbonOrientation;
  RibbonPlacement = RibbonPlacement;
  TextTransform = TextTransform;

  @Input() contest: ContestViewContest;
  @Input() entry: ContestViewEntry;
  @Input() freelancer: User;
  @Input() isContestOwner: boolean;
  @Input() hasContestAwardedEntry: boolean;

  readonly THUMBNAIL_LIMIT = 5;
  readonly DESCRIPTION_LIMIT = 200;

  ratingControl = new FormControl();
  ratingSubscription: Rx.Subscription;
  thumbnails: ReadonlyArray<ThumbnailViewerImage> = [];
  currentFileIndex = 0;

  tabs: ReadonlyArray<Tab> = [
    {
      title: 'Comments',
      selected: true,
    },
  ];

  rateResponse$: Promise<BackendResponse>;

  /**
   * Placeholder variables.
   * TODO: Replace variables with dynamic data -- hooked from the backend.
   */
  country = 'au';

  constructor(
    private datastore: Datastore,
    private modalRef: ModalRef<PreviewModalComponent>,
  ) {}

  ngOnInit() {
    this.ratingControl.setValue(this.entry.rating);

    this.ratingSubscription = this.ratingControl.valueChanges.subscribe(
      rating => {
        if (rating !== this.entry.rating) {
          this.onRateEntry({ rating });
        }
      },
    );

    this.thumbnails = this.entry.files.map(file => ({
      src: file.thumbnail80Url || '',
      alt: file.name,
    }));
  }

  onAwardEntry() {
    // TODO: Open award modal directly in preview modal after T82677 is done
    this.modalRef.close(PreviewModalResponse.AWARD);
  }

  onSlideChange(index: number) {
    this.currentFileIndex = index;
  }

  isSealed() {
    return this.entry.upgrades && this.entry.upgrades.sealed;
  }

  isHighlighted() {
    return (
      this.entry.upgrades &&
      this.entry.upgrades.highlight &&
      !this.hasContestAwardedEntry &&
      ![
        EntryStatusApi.ELIMINATED,
        EntryStatusApi.WITHDRAWN,
        EntryStatusApi.WITHDRAWN_ELIMINATED,
      ].includes(this.entry.status)
    );
  }

  isActive() {
    return (
      !this.hasContestAwardedEntry &&
      this.entry.status === EntryStatusApi.ACTIVE
    );
  }

  isRejected() {
    return [
      EntryStatusApi.ELIMINATED,
      EntryStatusApi.WITHDRAWN_ELIMINATED,
    ].includes(this.entry.status);
  }

  isAwarded() {
    return CONTEST_ENTRY_AWARD_STATUSES.includes(this.entry.status);
  }

  isBought() {
    return this.entry.status === EntryStatusApi.BOUGHT;
  }

  isBeingSold() {
    return (
      this.hasContestAwardedEntry &&
      this.entry.status === EntryStatusApi.ACTIVE &&
      (this.entry.sellPrice || this.contest.prize)
    );
  }

  isImage(filename: string | undefined) {
    if (!filename) {
      return false;
    }

    let fileType = filename.split('.').pop();
    fileType = fileType ? fileType.toLowerCase() : filename;
    return ['jpeg', 'jpg', 'png', 'gif'].includes(fileType);
  }

  isVideo(filename: string | undefined) {
    if (!filename) {
      return false;
    }

    let fileType = filename.split('.').pop();
    fileType = fileType ? fileType.toLowerCase() : filename;
    return ['mov', 'mpg', 'mpeg', 'avi', 'flv'].includes(fileType);
  }

  onRateEntry(changes: Partial<ContestViewEntry>) {
    this.rateResponse$ = this.updateEntry(changes);
  }

  updateEntry(changes: Partial<ContestViewEntry>) {
    return this.datastore
      .document('contestViewEntries', this.entry.id)
      .update(changes);
  }

  handleChatClick() {
    this.modalRef.close(PreviewModalResponse.START_CHAT);
  }

  ngOnDestroy() {
    this.ratingSubscription.unsubscribe();
  }
}
